// src/components/Navbar.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaUserCircle, FaBell, FaSignOutAlt, FaCog, FaUser, FaStore, FaCheck, FaTimes } from 'react-icons/fa';
import axiosInstance from '../utils/axiosInstance';
import ImplantToggle from './ImplantToggle';
import { subscribeChatSummary } from '../utils/chatSummary';
import './Navbar.css';

const DISMISSED_KEY = 'notif_dismissed_ids';
const READ_KEY = 'notif_read_ids';

const getStoredIds = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]');
  } catch {
    return [];
  }
};

const addStoredId = (key, id) => {
  const ids = getStoredIds(key);
  if (!ids.includes(id)) {
    ids.push(id);
    localStorage.setItem(key, JSON.stringify(ids));
  }
};

const Navbar = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showShopsDropdown, setShowShopsDropdown] = useState(false);
  const [selectedShop, setSelectedShop] = useState('Select Shop');
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllShops, setShowAllShops] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const profileMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const shopsDropdownRef = useRef(null);

  // ✅ NEW: ref to always call the latest fetchShops from event handlers
  const fetchShopsRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdminUser = user.type === 'SUPER_ADMIN';

  useEffect(() => {
    const storedShopId = localStorage.getItem('selectedShopId');
    const storedShopName = localStorage.getItem('selectedShopName');

    if (storedShopId && storedShopName) {
      setSelectedShopId(parseInt(storedShopId));
      setSelectedShop(storedShopName);
    }
  }, []);

  useEffect(() => {
    fetchShops();
  }, []);

  // ✅ UPDATED: extra event names + focus/visibility refresh (all go through ref)
  useEffect(() => {
    const refresh = () => fetchShopsRef.current && fetchShopsRef.current();

    const handleShopAdded = () => refresh();
    const handleShopUpdated = () => refresh();
    const handleShopDeleted = () => refresh();
    const handleShopsRefresh = () => refresh();
    const handleShopsUpdated = () => refresh();
    // ✅ NEW: safety-net event names in case shop creation page uses different names
    const handleShopCreated = () => refresh();
    const handleNewShop = () => refresh();

    // ✅ NEW: refresh whenever the window/tab regains focus
    const handleFocus = () => refresh();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    window.addEventListener('shopAdded', handleShopAdded);
    window.addEventListener('shopUpdated', handleShopUpdated);
    window.addEventListener('shopDeleted', handleShopDeleted);
    window.addEventListener('refreshShops', handleShopsRefresh);
    window.addEventListener('shopsUpdated', handleShopsUpdated);
    window.addEventListener('shopCreated', handleShopCreated);
    window.addEventListener('newShop', handleNewShop);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('shopAdded', handleShopAdded);
      window.removeEventListener('shopUpdated', handleShopUpdated);
      window.removeEventListener('shopDeleted', handleShopDeleted);
      window.removeEventListener('refreshShops', handleShopsRefresh);
      window.removeEventListener('shopsUpdated', handleShopsUpdated);
      window.removeEventListener('shopCreated', handleShopCreated);
      window.removeEventListener('newShop', handleNewShop);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    if (match) {
      const shopId = parseInt(match[1]);
      setSelectedShopId(shopId);

      const foundShop = shops.find(s => s.shop_id === shopId);
      if (foundShop) {
        setSelectedShop(foundShop.shop_name);
        localStorage.setItem('selectedShopId', shopId.toString());
        localStorage.setItem('selectedShopName', foundShop.shop_name);
      }
    }
  }, [location.pathname, shops]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (shopsDropdownRef.current && !shopsDropdownRef.current.contains(event.target)) {
        setShowShopsDropdown(false);
        setShowAllShops(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async (shopId) => {
    if (!shopId) return;
    try {
      const response = await axiosInstance.get('/order/notifications/', {
        params: { shop_id: shopId }
      });
      const dismissedIds = getStoredIds(DISMISSED_KEY);
      const readIds = getStoredIds(READ_KEY);

      const visible = (response.data || [])
        .filter(n => !dismissedIds.includes(n.id))
        .map(n => ({ ...n, read: n.read || readIds.includes(n.id) }));

      setNotifications(visible);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  useEffect(() => {
    if (selectedShopId) {
      fetchNotifications(selectedShopId);
    }
  }, [selectedShopId]);

  // keep the bell fresh: every 30 s, and straight away when a customer starts waiting for a person
  useEffect(() => {
    if (!selectedShopId) return undefined;
    const timer = setInterval(() => {
      if (!document.hidden) fetchNotifications(selectedShopId);
    }, 30000);
    let lastSig = null;
    const off = subscribeChatSummary((s) => {
      // changes when a customer starts (or stops) waiting
      const sig = (s.waiting_chats || []).map((c) => c.id).join(',');
      if (lastSig !== null && sig !== lastSig) fetchNotifications(selectedShopId);
      lastSig = sig;
    });
    return () => {
      clearInterval(timer);
      off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShopId]);

  const handleDismissNotification = (e, id) => {
    e.stopPropagation();
    addStoredId(DISMISSED_KEY, id);
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleMarkAllRead = () => {
    notifications.forEach(n => addStoredId(READ_KEY, n.id));
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleNotificationClick = (notif) => {
    addStoredId(READ_KEY, notif.id);
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
    setShowNotifications(false);

    if (!selectedShopId) return;

    if (notif.link_type === 'product') {
      navigate(`/shop/${selectedShopId}/products`);
    } else if (notif.link_type === 'customer') {
      navigate(`/shop/${selectedShopId}/inactive-customers`);
    } else if (notif.link_type === 'chat') {
      navigate(`/shop/${selectedShopId}/Chats`);
    } else if (notif.link_type === 'order') {
      if (notif.link_id) {
        navigate(`/shop/${selectedShopId}/order-detail/${notif.link_id}`);
      } else {
        navigate(`/shop/${selectedShopId}/orders`);
      }
    }
  };

  const fetchShops = async () => {
    try {
      setLoading(true);

      // ADMIN / STAFF: only their own shop, no shop list from the server
      if (!isSuperAdminUser) {
        if (user.shop_id) {
          const ownShop = { shop_id: parseInt(user.shop_id), shop_name: user.shop_name || 'My Shop' };
          setShops([ownShop]);
          setSelectedShop(ownShop.shop_name);
          setSelectedShopId(ownShop.shop_id);
          localStorage.setItem('selectedShopId', ownShop.shop_id.toString());
          localStorage.setItem('selectedShopName', ownShop.shop_name);
        }
        return;
      }

      const response = await axiosInstance.get('/user/shops/');
      const shopsData = response.data;
      setShops(shopsData);

      const urlMatch = location.pathname.match(/\/shop\/(\d+)/);
      const storedShopId = localStorage.getItem('selectedShopId');
      const storedShopName = localStorage.getItem('selectedShopName');

      if (urlMatch) {
        const shopId = parseInt(urlMatch[1]);
        const foundShop = shopsData.find(s => s.shop_id === shopId);
        if (foundShop) {
          setSelectedShop(foundShop.shop_name);
          setSelectedShopId(shopId);
          localStorage.setItem('selectedShopId', shopId.toString());
          localStorage.setItem('selectedShopName', foundShop.shop_name);
          return;
        }
      }

      if (storedShopId && storedShopName) {
        const foundShop = shopsData.find(s => s.shop_id === parseInt(storedShopId));
        if (foundShop) {
          setSelectedShop(foundShop.shop_name);
          setSelectedShopId(parseInt(storedShopId));
          return;
        } else {
          if (shopsData.length > 0) {
            const firstShop = shopsData[0];
            setSelectedShop(firstShop.shop_name);
            setSelectedShopId(firstShop.shop_id);
            localStorage.setItem('selectedShopId', firstShop.shop_id.toString());
            localStorage.setItem('selectedShopName', firstShop.shop_name);
          }
          return;
        }
      }

      if (shopsData.length > 0 && !selectedShopId) {
        const firstShop = shopsData[0];
        setSelectedShop(firstShop.shop_name);
        setSelectedShopId(firstShop.shop_id);
        localStorage.setItem('selectedShopId', firstShop.shop_id.toString());
        localStorage.setItem('selectedShopName', firstShop.shop_name);

        if (location.pathname === '/' || location.pathname === '') {
          navigate(`/shop/${firstShop.shop_id}`);
        }
      }
    } catch (err) {
      console.error('Error fetching shops:', err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: keep ref pointed at the latest fetchShops on every render
  fetchShopsRef.current = fetchShops;

  const handleLogout = () => {
    localStorage.clear();
    if (onLogout) onLogout();
    setShowProfileMenu(false);
    navigate('/signin');
  };

  const handleProfile = () => {
    navigate('/profile');
    setShowProfileMenu(false);
  };

  const handleShopSelect = (shop) => {
    setSelectedShop(shop.shop_name);
    setSelectedShopId(shop.shop_id);
    setShowShopsDropdown(false);
    setShowAllShops(false);

    localStorage.setItem('selectedShopId', shop.shop_id.toString());
    localStorage.setItem('selectedShopName', shop.shop_name);
    localStorage.setItem('selected_shop_id', shop.shop_id.toString());

    let currentPath = location.pathname;
    if (currentPath.includes('/shop/')) {
      const parts = currentPath.split('/');
      const pathWithoutShop = parts.filter((part, index) => index !== 1 && index !== 2).join('/');
      currentPath = pathWithoutShop || '/';
    }

    const newPath = `/shop/${shop.shop_id}${currentPath}`;
    navigate(newPath);

    window.dispatchEvent(new CustomEvent('shopChanged', {
      detail: { shopId: shop.shop_id, shop: shop }
    }));
  };

  const handleSwitchShopClick = (e) => {
    e.stopPropagation();
    setShowAllShops(!showAllShops);
  };

  const displayedShops = showAllShops ? shops : shops.slice(0, 4);
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-left"></div>

        <div className="navbar-right">
          {isSuperAdminUser && <ImplantToggle />}

          <div className="shops-dropdown-wrapper" ref={shopsDropdownRef}>
            {isSuperAdminUser ? (
              <button
                className="shops-btn"
                onClick={() => {
                  const willOpen = !showShopsDropdown;
                  setShowShopsDropdown(willOpen);
                  setShowAllShops(false);
                  // ✅ NEW: refetch fresh shop list every time dropdown is opened
                  if (willOpen) {
                    fetchShops();
                  }
                }}
              >
                <FaStore className="shops-icon" />
                <span className="shops-text">{selectedShop}</span>
                <span className={`shops-arrow ${showShopsDropdown ? 'rotate' : ''}`}>▼</span>
              </button>
            ) : (
              // Staff: only a badge with the assigned shop name - not clickable, no shop switch
              <div
                className="shops-btn"
                style={{ cursor: 'default', pointerEvents: 'none', userSelect: 'none' }}
                title="Your assigned shop"
              >
                <FaStore className="shops-icon" />
                <span className="shops-text">{selectedShop}</span>
              </div>
            )}

            {isSuperAdminUser && showShopsDropdown && (
              <div className="shops-dropdown">
                <div className="shops-dropdown-header">
                  <div className="header-top-row">
                    <div className="header-avatar">{selectedShop.charAt(0).toUpperCase()}</div>
                    <div className="header-title-group">
                      <h3 className="shops-main-title">{selectedShop}</h3>
                      <p className="shops-subtitle">Shop ID: {selectedShopId || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="switch-shop-label-top">SWITCH SHOP</div>

                  {shops.length > 4 && (
                    <div className="switch-shop-bottom-row clickable" onClick={handleSwitchShopClick}>
                      <span className="switch-desc-text">
                        {showAllShops ? 'Show Less Shops' : 'Switch to Another Shop'}
                      </span>
                      <span className="options-count-badge">
                        {shops.length} options
                        <span className={`toggle-arrow ${showAllShops ? 'up' : 'down'}`}>
                          {showAllShops ? ' ▲' : ' ▼'}
                        </span>
                      </span>
                    </div>
                  )}
                </div>

                <div className="shops-vertical-list">
                  {loading ? (
                    <div className="shops-loading">
                      <div className="spinner-tiny"></div>
                      <span>Loading...</span>
                    </div>
                  ) : (
                    displayedShops.map((shop, index) => (
                      <button
                        key={shop.shop_id}
                        className={`shop-list-item ${selectedShopId === shop.shop_id ? 'selected' : ''}`}
                        onClick={() => handleShopSelect(shop)}
                      >
                        <div className="shop-list-left">
                          <div className={`shop-list-letter letter-color-${(index + 1) % 4}`}>
                            {shop.shop_name.charAt(0).toUpperCase()}
                          </div>
                          <span className="shop-list-name">{shop.shop_name}</span>
                        </div>
                        {selectedShopId === shop.shop_id && (
                          <span className="check-icon-list"><FaCheck /></span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="notification-wrapper" ref={notificationRef}>
            <button
              className="notification-btn"
              onClick={() => {
                setShowNotifications(!showNotifications);
                if (!showNotifications && selectedShopId) {
                  fetchNotifications(selectedShopId);
                }
              }}
            >
              <FaBell className="notification-icon" />
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>

            {showNotifications && (
              <div className="notification-dropdown">
                <div className="notification-header">
                  <h3>Notifications</h3>
                  <button className="mark-all-btn" onClick={handleMarkAllRead}>Mark all as read</button>
                </div>
                <div className="notification-list">
                  {notifications.length === 0 ? (
                    <div className="notification-item">
                      <div className="notification-content">
                        <p className="notification-title">No notifications right now</p>
                      </div>
                    </div>
                  ) : (
                    notifications.map(notif => (
                      <div
                        key={notif.id}
                        className={`notification-item ${!notif.read ? 'unread' : ''}`}
                        onClick={() => handleNotificationClick(notif)}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      >
                        <div className="notification-content">
                          <p className="notification-title">{notif.title}</p>
                          <span className="notification-time">{notif.time}</span>
                        </div>
                        <button
                          className="notification-dismiss-btn"
                          onClick={(e) => handleDismissNotification(e, notif.id)}
                          title="Dismiss"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', color: '#888' }}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="profile-wrapper" ref={profileMenuRef}>
            <button className="profile-btn" onClick={() => setShowProfileMenu(!showProfileMenu)}>
              {user.avatar ? (
                <img src={user.avatar} alt="Profile" className="profile-avatar" />
              ) : (
                <FaUserCircle className="profile-icon" />
              )}
              <span className="profile-name">{user.first_Name || user.name || 'Admin'}</span>
              <span className="dropdown-arrow">▼</span>
            </button>

            {showProfileMenu && (
              <div className="profile-dropdown">
                <div className="profile-dropdown-header">
                  <div className="profile-dropdown-avatar">
                    {user.avatar ? <img src={user.avatar} alt="Profile" /> : <FaUserCircle />}
                  </div>
                  <div className="profile-dropdown-info">
                    <h4>{user.first_Name ? `${user.first_Name} ${user.last_Name || ''}` : 'Admin User'}</h4>
                    <p>{user.email || 'admin@hira-tech.com'}</p>
                  </div>
                </div>
                <div className="profile-dropdown-menu">
                  <button onClick={handleProfile} className="dropdown-item">
                    <FaUser /><span>My Profile</span>
                  </button>
                  <button onClick={() => navigate('/settings')} className="dropdown-item">
                    <FaCog /><span>Settings</span>
                  </button>
                  <div className="dropdown-divider"></div>
                  <button onClick={handleLogout} className="dropdown-item logout-item">
                    <FaSignOutAlt /><span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;